import { createClient, Entry, type Concept } from 'contentful';
import { contentfulConfig } from '../contentful';
import { PAGE_MODEL_CONTENT_TYPE_IDS, type PageModel } from '../queries/page/types';
import { logPipelineEvent, now } from 'libs/experimentation/observability';

type ContentfulClientWithoutLinkResolution = ReturnType<typeof createClient>['withoutLinkResolution'];

let productionClient: ContentfulClientWithoutLinkResolution | null = null;
let previewClient: ContentfulClientWithoutLinkResolution | null = null;

function getProductionClient() {
  if (!productionClient) {
    productionClient = createClient({
      space: contentfulConfig.spaceId,
      accessToken: contentfulConfig.accessToken,
      environment: contentfulConfig.environment,
    }).withoutLinkResolution;
  }
  return productionClient;
}

function getPreviewClient() {
  if (!previewClient) {
    previewClient = createClient({
      space: contentfulConfig.spaceId,
      accessToken: contentfulConfig.previewAccessToken,
      environment: contentfulConfig.environment,
      host: 'preview.contentful.com',
    }).withoutLinkResolution;
  }
  return previewClient;
}

function getClient(draftPreviewEnabled: boolean) {
  return draftPreviewEnabled ? getPreviewClient() : getProductionClient();
}

interface RESTFetchResult {
  entry: Entry | null;
  includes: {
    Entry?: unknown[];
    Asset?: unknown[];
  };
  durationMs: number;
}

interface RESTFetchEntriesResult {
  entries: Entry[];
  includes: {
    Entry?: unknown[];
    Asset?: unknown[];
  };
  durationMs: number;
}

/**
 * Fetches a page by slug using the Contentful REST SDK.
 * Tries multiple page content types based on the provided models array.
 *
 * @param slug - The page slug to fetch
 * @param draftPreviewEnabled - Whether to use preview mode
 * @param models - Array of page models to search (defaults to all models)
 * @returns The entry and duration in milliseconds
 */
export async function fetchPageBySlugREST(
  slug: string,
  draftPreviewEnabled: boolean,
  models: PageModel[] = ['Page', 'PaidPage', 'BlogPostPage', 'PageBlogPostFlexible', 'AgreementPage'],
): Promise<RESTFetchResult> {
  const startTimeMs = now();
  const client = getClient(draftPreviewEnabled);

  // Convert PageModel array to content type IDs
  // For each content type, check both slug variations in a single query using [in] operator
  const contentTypes = models.map(pageModel => PAGE_MODEL_CONTENT_TYPE_IDS[pageModel]);
  const slugVariations = [slug, `${slug}/`];

  for (const contentType of contentTypes) {
    const response = await client.getEntries({
      content_type: contentType,
      'fields.slug[in]': slugVariations.join(','),
      // Max depth (10) ensures variant entries for both module-level and section group swaps are pre-fetched in the includes map.
      include: 10,
      limit: 1,
    });

    if (response.items.length > 0) {
      const durationMs = now() - startTimeMs;
      logPipelineEvent({
        event: 'contentful.pageFetch',
        durationMs,
      });
      return {
        entry: response.items[0],
        includes: {
          Entry: response.includes?.Entry ?? [],
          Asset: response.includes?.Asset ?? [],
        },
        durationMs,
      };
    }
  }

  // No page found with any content type
  const durationMs = now() - startTimeMs;
  logPipelineEvent({
    event: 'contentful.pageFetch',
    durationMs,
  });
  return {
    entry: null,
    includes: { Entry: [], Asset: [] },
    durationMs,
  };
}

/**
 * Content types that can be fetched by taxonomy for ArticleGrid.
 */
export type ArticleGridContentType = 'pageBlogPostFlexible' | 'blogPage' | 'resource';

interface FetchEntriesByTaxonomyOptions {
  /** Content types to fetch (defaults to all supported types) */
  contentTypes?: ArticleGridContentType[];
  /** Maximum number of entries to return per content type (defaults to 10) */
  limit?: number;
  /** Current subdomain — when set, strips the matching prefix from entry slugs (e.g. 'enterprise' strips 'enterprise/') */
  subdomain?: string;
}

/**
 * Fetches entries by taxonomy concept ID using the Contentful REST SDK.
 * Supports multiple content types: PageBlogPostFlexible, BlogPostPage (blogPage), and Resource.
 *
 * When `subdomain` is provided, rewrites slug fields on fetched entries and their linked
 * includes to strip the matching prefix (e.g. subdomain 'enterprise' strips 'enterprise/'
 * from slugs). This ensures taxonomy-fetched articles produce correct URLs on subdomains
 * without relying on the page-level `transformPageLinks` pipeline.
 *
 * @param taxonomyId - The taxonomy concept ID to filter by
 * @param draftPreviewEnabled - Whether to use preview mode
 * @param options - Optional configuration (contentTypes, limit, subdomain)
 * @returns The entries, includes, and duration in milliseconds
 */
export async function fetchEntriesByTaxonomyREST(
  taxonomyId: string,
  draftPreviewEnabled: boolean,
  options: FetchEntriesByTaxonomyOptions = {},
): Promise<RESTFetchEntriesResult> {
  const { contentTypes = ['pageBlogPostFlexible', 'blogPage', 'resource'], limit = 200, subdomain } = options;
  const startTimeMs = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const client = getClient(draftPreviewEnabled);

  // Fetch entries for each content type in parallel
  const fetchPromises = contentTypes.map(contentType =>
    client.getEntries({
      content_type: contentType,
      'metadata.concepts.descendants[in]': taxonomyId,
      include: 2,
      limit,
      order: ['-sys.createdAt'],
    } as Parameters<typeof client.getEntries>[0]),
  );

  const responses = await Promise.all(fetchPromises);

  // Combine all entries and includes from all responses
  const allEntries: Entry[] = [];
  const allIncludesEntries: unknown[] = [];
  const allIncludesAssets: unknown[] = [];

  for (const response of responses) {
    allEntries.push(...response.items);
    if (response.includes?.Entry) {
      allIncludesEntries.push(...response.includes.Entry);
    }
    if (response.includes?.Asset) {
      allIncludesAssets.push(...response.includes.Asset);
    }
  }

  // Sort combined entries newest-first, preferring the editorial publishDate field over
  // sys.createdAt so that authors can control article ordering independently of when
  // the Contentful entry was created.
  allEntries.sort((a, b) => {
    const fields = (a.fields ?? {}) as Record<string, unknown>;
    const fieldsB = (b.fields ?? {}) as Record<string, unknown>;
    const dateA = new Date((fields.publishDate as string | undefined) ?? a.sys.createdAt).getTime();
    const dateB = new Date((fieldsB.publishDate as string | undefined) ?? b.sys.createdAt).getTime();
    return dateB - dateA;
  });

  // Strip subdomain prefix from slug fields (e.g. 'enterprise/' on enterprise subdomain).
  // This covers main entry slugs and linked entry slugs (e.g. resourcePage)
  // that the adapter resolves from the includes map.
  if (subdomain) {
    stripSubdomainPrefixFromSlugs(allEntries, allIncludesEntries, subdomain);
  }

  const durationMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startTimeMs;
  return {
    entries: allEntries,
    includes: {
      Entry: allIncludesEntries,
      Asset: allIncludesAssets,
    },
    durationMs,
  };
}

/**
 * Strips a leading subdomain prefix from a slug.
 * For subdomain 'enterprise', strips 'enterprise/' from the start, or 'enterprise' if
 * it is the entire slug. Requires a `/` separator (or end-of-string) so that slugs like
 * 'enterprise-wellness-guide' are left untouched.
 */
function stripSubdomainPrefix(slug: string, subdomain: string): string {
  if (!slug) return '';
  const pattern = new RegExp(`^${subdomain}(?:/|$)`);
  return slug.trim().replace(pattern, '');
}

/**
 * Returns true when `value` is a non-null plain object (not an array).
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Recursively traverses a value graph and rewrites `fields.slug` on any
 * Contentful entry objects found at any nesting depth.
 *
 * An object is treated as a Contentful entry when it has a `fields` property
 * that is itself a plain object. Only `fields.slug` string values are mutated.
 *
 * Uses a `WeakSet` to safely handle cyclic / shared references created by
 * Contentful's link resolution.
 */
function rewriteEntrySlugFields(value: unknown, subdomain: string, visited: WeakSet<object>): void {
  if (!isRecord(value)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        rewriteEntrySlugFields(item, subdomain, visited);
      }
    }
    return;
  }

  if (visited.has(value)) return;
  visited.add(value);

  // If this object looks like a Contentful entry (has `fields` object), rewrite its slug.
  const fields = value.fields;
  if (isRecord(fields)) {
    if (typeof fields.slug === 'string') {
      fields.slug = stripSubdomainPrefix(fields.slug, subdomain);
    }
    // Recurse into all field values to catch inline-resolved linked entries.
    for (const fieldValue of Object.values(fields)) {
      rewriteEntrySlugFields(fieldValue, subdomain, visited);
    }
  }
}

/**
 * Rewrites `fields.slug` on entries and included entries to strip the subdomain prefix.
 * Traverses the full object graph so inline-resolved linked entries are also rewritten.
 * Mutates the entry objects in-place so the adapter picks up corrected slugs.
 */
function stripSubdomainPrefixFromSlugs(entries: Entry[], includesEntries: unknown[], subdomain: string): void {
  const visited = new WeakSet<object>();
  for (const entry of entries) {
    rewriteEntrySlugFields(entry, subdomain, visited);
  }
  for (const include of includesEntries) {
    rewriteEntrySlugFields(include, subdomain, visited);
  }
}

/**
 * Resolved taxonomy concept with its label and parent IDs.
 */
export interface ResolvedConcept {
  id: string;
  label: string;
  uri?: string;
  /** IDs of immediate parent concepts (from `broader`). Empty for top-level concepts. */
  broaderIds: string[];
}

/**
 * Extracts a map of entry sys.id to its tagged taxonomy concept IDs from raw REST entries.
 */
export function extractEntryConceptIds(entries: Entry[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const entry of entries) {
    const entryId = entry.sys?.id;
    if (!entryId) continue;

    const metadata = entry.metadata as { concepts?: Array<{ sys: { id: string } }> } | undefined;
    const conceptIds = metadata?.concepts?.map(c => c.sys.id) ?? [];
    map.set(entryId, conceptIds);
  }
  return map;
}

/**
 * Fetches taxonomy concepts from the CDA and returns their labels and hierarchy info.
 * Uses `getConcept` per ID (in parallel) since the CDA concepts collection endpoint
 * does not support filtering by ID.
 *
 * Always uses the delivery API — taxonomy concepts are not draft/published content and
 * are unavailable on the preview API (`preview.contentful.com`).
 */
export async function fetchConceptDetails(conceptIds: string[]): Promise<Map<string, ResolvedConcept>> {
  const uniqueIds = [...new Set(conceptIds)];
  if (uniqueIds.length === 0) return new Map();

  const results = await Promise.allSettled(uniqueIds.map(id => getProductionClient().getConcept(id)));
  const conceptMap = new Map<string, ResolvedConcept>();

  for (const result of results) {
    if (result.status === 'fulfilled') {
      const concept: Concept<'en-US'> = result.value;
      const label = concept.prefLabel?.['en-US'] ?? concept.sys.id;
      const broaderIds = concept.broader?.map(b => b.sys.id) ?? [];
      conceptMap.set(concept.sys.id, { id: concept.sys.id, label, broaderIds, uri: concept.uri });
    }
  }

  return conceptMap;
}

const CONCEPTS_PAGE_LIMIT = 100;

/**
 * Fetches all taxonomy concepts from the CDA using cursor pagination.
 * Uses the delivery API only (taxonomy is not available on preview).
 * Use for nav or filters that need the full list of concepts.
 */
export async function fetchAllConcepts(options?: {
  conceptScheme?: string;
  order?: ('sys.createdAt' | 'sys.updatedAt' | 'prefLabel')[];
}): Promise<ResolvedConcept[]> {
  const all: ResolvedConcept[] = [];
  let nextPage: string | undefined;

  type Client = ReturnType<typeof getProductionClient>;
  type ConceptsQuery = Parameters<Client['getConcepts']>[0];
  do {
    const query: ConceptsQuery = {
      limit: CONCEPTS_PAGE_LIMIT,
      ...(options?.order && { order: options.order }),
      ...(options?.conceptScheme && { conceptScheme: options.conceptScheme }),
    };
    if (nextPage) query.nextPage = nextPage;

    const response = await getProductionClient().getConcepts(query);
    for (const concept of response.items) {
      const c = concept as Concept<'en-US'>;
      const label = c.prefLabel?.['en-US'] ?? c.sys.id;
      const broaderIds = c.broader?.map(b => b.sys.id) ?? [];
      all.push({ id: c.sys.id, label, broaderIds, uri: c.uri });
    }
    nextPage = response.pages?.next;
  } while (nextPage);

  return all;
}
